import { IDecaCredits, IDecaDocument, IDecaDocumentCompany, IDecaDocumentSegment } from '@okcargo-apps/shared-domain';
import {
  AvailableCompanyCreditsResponse,
  DigitalControlDocCompanyResponse,
  DigitalControlDocResponse,
  DigitalControlDocSegmentPayload,
} from '@okcargo/query-processor';

export function transformToIDecaDocument(dto: DigitalControlDocResponse): IDecaDocument {
  return {
    id: dto.id,
    reference: dto.reference,
    shipper: {
      id: dto.shipperId,
      companyName: dto.shipperName,
      taxId: dto.shipperTaxId,
      street: dto.shipperAddressStreet,
      city: dto.shipperAddressCity,
      postalCode: dto.shipperAddressPostalCode,
      stateCode: dto.shipperAddressStateCode,
      countryCode: dto.shipperAddressCountryCode,
      contactId: dto.shipperContactId,
      contactFirstName: dto.shipperContactFirstName,
      contactLastName: dto.shipperContactLastName,
      contactEmail: dto.shipperContactEmail,
      contactPhone: dto.shipperContactPhone,
    },
    carrier: {
      id: dto.carrierId,
      companyName: dto.carrierName,
      taxId: dto.carrierTaxId,
      street: dto.carrierAddressStreet,
      city: dto.carrierAddressCity,
      postalCode: dto.carrierAddressPostalCode,
      stateCode: dto.carrierAddressStateCode,
      countryCode: dto.carrierAddressCountryCode,
      contactId: dto.carrierContactId,
      contactFirstName: dto.carrierContactFirstName,
      contactLastName: dto.carrierContactLastName,
      contactEmail: dto.carrierContactEmail,
      contactPhone: dto.carrierContactPhone,
    },

    segments: transformToIDecaDocumentSegmentList(dto.segments),

    createdByContactId: dto.createdByContactId,
    createdByCompanyId: dto.createdByCompanyId,
    pdfUrl: dto.pdfUrl,
    qrUrl: dto.qrUrl,
    specialAuthorizationType: dto.specialAuthorizationType,
    tractorPlateNumber: dto.tractorPlateNumber,
    trailerPlateNumber: dto.trailerPlateNumber,
    reservations: dto.reservations ?? [],
    // TODO add mapping to IDecaEventLog from DigitalControlDocEventLogEntryPayload
    eventLog: [],

    createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
    updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : undefined,
  };
}

export function transformToIDecaDocumentList(dtos: DigitalControlDocResponse[]): IDecaDocument[] {
  return (dtos ?? []).map((dto) => transformToIDecaDocument(dto));
}

function transformToIDecaDocumentSegment(dto: DigitalControlDocSegmentPayload): IDecaDocumentSegment {
  return {
    id: dto.id,
    origin: {
      companyName: dto.origin.companyName,
      street: dto.origin.street,
      city: dto.origin.city,
      date: new Date(dto.origin.date),
      postalCode: dto.origin.postalCode,
      stateCode: dto.origin.stateCode,
      countryCode: dto.origin.countryCode,
    },
    destination: {
      companyName: dto.destination.companyName,
      street: dto.destination.street,
      city: dto.destination.city,
      date: new Date(dto.destination.date),
      postalCode: dto.destination.postalCode,
      stateCode: dto.destination.stateCode,
      countryCode: dto.destination.countryCode,
    },
    goodsDescription: dto.goodsDescription,
    netWeight: dto.netWeight,
  };
}

function transformToIDecaDocumentSegmentList(dtos: DigitalControlDocSegmentPayload[]): IDecaDocumentSegment[] {
  return (dtos ?? []).map((dto) => transformToIDecaDocumentSegment(dto));
}

export function transformToIDecaCompany(dto: DigitalControlDocCompanyResponse): IDecaDocumentCompany {
  return {
    id: dto.id,
    companyName: dto.name,
    taxId: dto.taxId,
    street: dto.addressStreet,
    city: dto.addressCity,
    postalCode: dto.addressPostalCode,
    stateCode: dto.addressStateCode,
    countryCode: 'ES', // TODO review
    contactId: undefined, // TODO review
    contactFirstName: dto.contactFirstName,
    contactLastName: dto.contactLastName,
    contactEmail: dto.contactEmail,
    contactPhone: dto.contactPhone,
    isShipper: dto.shipper,
    isCarrier: dto.carrier,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function transformToIDecaCompanyList(dtos: DigitalControlDocCompanyResponse[]): IDecaDocumentCompany[] {
  return (dtos ?? []).map((dto) => transformToIDecaCompany(dto));
}

export function transformToIDecaCredits(dto: AvailableCompanyCreditsResponse): IDecaCredits {
  return {
    availableCredits: dto.availableDcdCredits,
    promotionalCredits: dto.promotionalCredits,
    promotionalCreditsExpireAt: dto.promotionalCreditsExpireAt ? new Date(dto.promotionalCreditsExpireAt) : undefined,
  };
}
